import React from 'react';
import { Trash, Quotes } from '@phosphor-icons/react';
import { getUnitLabel } from '../../../../utils/boqHelpers';
import { formatCurrencyShort, formatDelta } from '../../../../utils/formatters';
import styles from './HomeChoices.module.css';

function ProgressBar({ configured, total }) {
  const pct = total > 0 ? (configured / total) * 100 : 0;
  return (
    <div className={styles.progressBar}>
      <div className={styles.progressFill} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ==========================================================================
   Option Chips
   ========================================================================== */

function OptionChips({ item, draftChanges, onOptionSelect }) {
  const draft = draftChanges[item.master_item_id];

  const getDisplayValue = group => {
    if (draft?.selectedOption?.[group] !== undefined) {
      return draft.selectedOption[group];
    }
    return item.selected_option?.[group];
  };

  switch (item.category) {
    case 'choice_bundle':
      return (
        <div className={styles.chipSection}>
          <div className={styles.chipRow}>
            {item.bundle_children.map(child => {
              const isSelected = getDisplayValue('selected_child_id') === child.id;
              return (
                <button
                  key={child.id}
                  className={isSelected ? styles.chipSelected : styles.chip}
                  onClick={() => onOptionSelect(item.master_item_id, 'selected_child_id', child.id)}
                  type="button"
                >
                  <span className={styles.chipName}>{child.name}</span>
                  {child.estimated_cost != null && (
                    <span className={styles.chipCost}>
                      {formatCurrencyShort(child.estimated_cost)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );

    case 'multi_group':
      return (
        <div className={styles.chipSection}>
          {Object.entries(item.option_groups).map(([groupName, options]) => (
            <div key={groupName} style={{ marginBottom: '8px' }}>
              <span className={styles.chipGroupLabel}>{groupName}</span>
              <div className={styles.chipRow}>
                {options.map(opt => {
                  const isSelected = getDisplayValue(groupName) === opt.name;
                  return (
                    <button
                      key={opt.name}
                      className={isSelected ? styles.chipSelected : styles.chip}
                      onClick={() => onOptionSelect(item.master_item_id, groupName, opt.name)}
                      type="button"
                    >
                      <span className={styles.chipName}>{opt.name}</span>
                      {opt.default_cost != null && (
                        <span className={styles.chipCost}>
                          {formatCurrencyShort(opt.default_cost)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );

    case 'single_group': {
      const groups = Object.entries(item.option_groups);
      const groupName = groups.length > 0 ? groups[0][0] : 'default';
      const options = groups.length > 0 ? groups[0][1] : [];
      return (
        <div className={styles.chipSection}>
          <div className={styles.chipRow}>
            {options.map(opt => {
              const isSelected = getDisplayValue(groupName) === opt.name;
              return (
                <button
                  key={opt.name}
                  className={isSelected ? styles.chipSelected : styles.chip}
                  onClick={() => onOptionSelect(item.master_item_id, groupName, opt.name)}
                  type="button"
                >
                  <span className={styles.chipName}>{opt.name}</span>
                  {opt.default_cost != null && (
                    <span className={styles.chipCost}>{formatCurrencyShort(opt.default_cost)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'no_options':
    default:
      return null;
  }
}

/* ==========================================================================
   Choice Card
   ========================================================================== */

const ChoiceCard = React.memo(function ChoiceCard({
  item,
  draftChanges,
  deltas,
  itemCounts,
  costTypeMap,
  vendorPriceMap,
  localInputs,
  onLocalCostChange,
  onLocalNotesChange,
  onCostBlur,
  onNotesBlur,
  onOptionSelect,
  onDelete,
  onQuote,
}) {
  const draft = draftChanges[item.master_item_id];
  const isChanged = Boolean(draft);
  const itemDelta = deltas[item.master_item_id];
  const counts = itemCounts[item.master_item_id] || { total: 0, quoted: 0 };

  // Determine if configured (has any option selection in draft or saved)
  const hasSelection = (() => {
    if (draft?.selectedOption) {
      return Object.entries(draft.selectedOption).some(
        ([key, val]) => key !== 'cost' && key !== 'notes' && val,
      );
    }
    if (!item.selected_option) return false;
    return Object.entries(item.selected_option).some(
      ([key, val]) => key !== 'cost' && key !== 'notes' && val,
    );
  })();

  // Effective cost for display
  const effectiveCost =
    draft?.cost !== undefined && draft?.cost !== null
      ? draft.cost
      : item.selected_option?.cost || null;

  // Cost type label
  const costTypeName = costTypeMap?.[item.cost_type_id];
  const unitLabel = getUnitLabel(costTypeName);

  // Total impact
  const totalImpact = effectiveCost && counts.total > 0 ? effectiveCost * counts.total : null;

  // Vendor price
  const vendorInfo = vendorPriceMap?.get(item.master_item_id);

  // Card class
  const cardClass = isChanged
    ? styles.choiceCardChanged
    : hasSelection
      ? styles.choiceCard
      : styles.choiceCardUnconfigured;

  return (
    <div className={cardClass}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className={styles.itemName}>{item.item_name}</span>
          {isChanged && itemDelta && itemDelta.costDelta !== 0 && (
            <span
              className={
                itemDelta.costDelta > 0 ? styles.deltaBadgePositive : styles.deltaBadgeNegative
              }
            >
              {formatDelta(itemDelta.costDelta)}
            </span>
          )}
        </div>
        {counts.total > 0 && totalImpact ? (
          <span className={styles.impactBadge}>
            {counts.total} {unitLabel} = {formatCurrencyShort(totalImpact)}
          </span>
        ) : counts.total > 0 ? (
          <span className={styles.impactBadge}>
            {counts.total} {unitLabel}
          </span>
        ) : null}
      </div>

      {/* Option Chips */}
      {item.category !== 'no_options' ? (
        <OptionChips item={item} draftChanges={draftChanges} onOptionSelect={onOptionSelect} />
      ) : (
        <div className={styles.chipSection}>
          <span className={styles.callToAction}>ללא אפשרויות</span>
        </div>
      )}

      {!hasSelection && item.category !== 'no_options' && (
        <div className={styles.chipSection}>
          <span className={styles.callToAction}>בחר אפשרות</span>
        </div>
      )}

      {/* Footer: cost, notes, actions */}
      <div className={styles.cardFooter}>
        <div className={styles.costSection}>
          <div className={styles.costRow}>
            <div className={styles.costInputWrapper}>
              <input
                type="text"
                inputMode="numeric"
                value={localInputs[item.id]?.cost || ''}
                onChange={e => onLocalCostChange(item.id, e.target.value)}
                onBlur={() => onCostBlur(item)}
                placeholder="מחיר"
                className={styles.costInput}
                disabled={!hasSelection}
              />
              <span className={styles.currencySymbol}>₪</span>
            </div>
            {unitLabel && unitLabel !== '—' && (
              <span className={styles.costTypeLabel}>{unitLabel}</span>
            )}
          </div>
          {vendorInfo && (
            <div className={styles.vendorPrice} title={vendorInfo.vendorName || 'הצעת ספק'}>
              <span>הצעת ספק:</span>
              <span style={{ fontWeight: 600 }}>{formatCurrencyShort(vendorInfo.price)}</span>
            </div>
          )}
        </div>

        <input
          type="text"
          value={localInputs[item.id]?.notes || ''}
          onChange={e => onLocalNotesChange(item.id, e.target.value)}
          onBlur={() => onNotesBlur(item)}
          placeholder="הערות"
          className={styles.notesInput}
          disabled={!hasSelection}
        />

        <div className={styles.cardActions}>
          {item.item_type_id && onQuote && (
            <button
              className={styles.quoteButton}
              onClick={() => onQuote(item.item_type_id)}
              title="הצעות מחיר"
              type="button"
            >
              <Quotes size={16} />
            </button>
          )}
          {item.id && (
            <button
              className={styles.deleteButton}
              onClick={() => onDelete(item.id)}
              title="מחק בחירה"
              type="button"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

/* ==========================================================================
   Main Component
   ========================================================================== */

const HomeChoicesCards = React.memo(function HomeChoicesCards({
  groupedData,
  summaryStats,
  vendorPriceMap,
  onOptionSelect,
  onCostChange,
  onNotesChange,
  onDelete,
  onQuote,
  draftChanges,
  deltas,
  itemCounts,
  costTypeMap,
  changeCount,
}) {
  // Local state for cost and notes inputs (save on blur)
  const [localInputs, setLocalInputs] = React.useState({});

  // Build all items flat for initializing local inputs
  const allItems = React.useMemo(() => groupedData.flatMap(g => g.items), [groupedData]);

  // Stable key that changes only when saved options change (not on every render)
  const optionsKey = React.useMemo(
    () => allItems.map(i => `${i.id}:${JSON.stringify(i.selected_option)}`).join(','),
    [allItems],
  );

  // Initialize local state from selected_option / draft
  React.useEffect(() => {
    const inputs = {};
    allItems.forEach(item => {
      const draft = draftChanges[item.master_item_id];
      const cost =
        draft?.cost !== undefined ? (draft.cost ?? '') : item.selected_option?.cost || '';
      inputs[item.id] = {
        cost,
        notes: item.selected_option?.notes || '',
      };
    });
    setLocalInputs(inputs);
  }, [optionsKey, draftChanges]);

  const handleLocalCostChange = (itemId, value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    setLocalInputs(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], cost: numericValue },
    }));
  };

  const handleLocalNotesChange = (itemId, value) => {
    setLocalInputs(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes: value },
    }));
  };

  const handleCostBlur = item => {
    const cost = localInputs[item.id]?.cost;
    if (cost !== undefined) {
      onCostChange(item.id, cost);
    }
  };

  const handleNotesBlur = item => {
    if (!item.selected_option) return;
    const notes = localInputs[item.id]?.notes;
    if (notes !== undefined) {
      onNotesChange(item.id, notes);
    }
  };

  return (
    <div dir="rtl">
      {/* Compact summary line */}
      <div className={styles.summaryLine}>
        <span>
          {summaryStats.totalConfigured} / {summaryStats.totalItems} פריטים הוגדרו
        </span>
        {summaryStats.estimatedTotal > 0 && (
          <span>עלות משוערת: {formatCurrencyShort(summaryStats.estimatedTotal)}</span>
        )}
        {changeCount > 0 && (
          <span className={styles.unsavedBadge}>{changeCount} שינויים לא נשמרו</span>
        )}
      </div>
      <ProgressBar configured={summaryStats.totalConfigured} total={summaryStats.totalItems} />

      {/* Flat list of cards — no category grouping */}
      <div className={styles.flatCardList}>
        {allItems.map(item => (
          <ChoiceCard
            key={item.master_item_id}
            item={item}
            draftChanges={draftChanges}
            deltas={deltas}
            itemCounts={itemCounts}
            costTypeMap={costTypeMap}
            vendorPriceMap={vendorPriceMap}
            localInputs={localInputs}
            onLocalCostChange={handleLocalCostChange}
            onLocalNotesChange={handleLocalNotesChange}
            onCostBlur={handleCostBlur}
            onNotesBlur={handleNotesBlur}
            onOptionSelect={onOptionSelect}
            onDelete={onDelete}
            onQuote={onQuote}
          />
        ))}
      </div>
    </div>
  );
});

export default HomeChoicesCards;
